import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Star, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { ALL_CATS } from "@/i18n/slugs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPE_LABELS = Object.fromEntries(ALL_CATS.map((c) => [c.key, c.fr.name]));

export default function ReviewPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [reviewInfo, setReviewInfo] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [reviewerName, setReviewerName] = useState("");

  useEffect(() => {
    checkToken();
  }, []);

  const checkToken = async () => {
    try {
      const res = await axios.get(`${API}/reviews/check/${token}`);
      setReviewInfo(res.data);
    } catch {
      setInvalid(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      toast.error("Veuillez sélectionner une note");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews/submit`, {
        token,
        rating,
        comment,
        reviewer_name: reviewerName
      });
      setSubmitted(true);
      toast.success("Merci pour votre avis !");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (invalid) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Lien invalide</h1>
          <p className="text-slate-600">Ce lien d'avis est invalide ou a déjà été utilisé.</p>
          <a href="/" className="text-orange-500 hover:underline mt-4 inline-block">Retour à l'accueil</a>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md" data-testid="review-success">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Merci pour votre avis !</h1>
          <p className="text-slate-600">Votre avis sera publié après validation par notre équipe.</p>
          <a href="/" className="text-orange-500 hover:underline mt-4 inline-block">Retour à l'accueil</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12" data-testid="review-page">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Star className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Donnez votre avis
          </h1>
          <p className="text-slate-600 mt-2">
            Sur <strong>{reviewInfo?.contractor_name}</strong>
            {reviewInfo?.project_type && (
              <span> — {PROJECT_TYPE_LABELS[reviewInfo.project_type] || reviewInfo.project_type}</span>
            )}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-8 border border-slate-100">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="review-form">
            {/* Star Rating */}
            <div>
              <Label className="text-slate-700 mb-3 block">Note</Label>
              <div className="flex justify-center gap-2" data-testid="star-rating">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="p-1 transition-transform hover:scale-110"
                    data-testid={`star-${star}`}
                  >
                    <Star
                      className={`w-10 h-10 transition-colors ${
                        star <= (hoverRating || rating)
                          ? "fill-yellow-400 text-yellow-400"
                          : "text-slate-300"
                      }`}
                    />
                  </button>
                ))}
              </div>
              {rating > 0 && (
                <p className="text-center text-sm text-slate-500 mt-2">
                  {rating === 1 && "Très insatisfait"}
                  {rating === 2 && "Insatisfait"}
                  {rating === 3 && "Correct"}
                  {rating === 4 && "Satisfait"}
                  {rating === 5 && "Très satisfait"}
                </p>
              )}
            </div>

            {/* Name */}
            <div>
              <Label htmlFor="reviewerName" className="text-slate-700 mb-2 block">
                Votre nom
              </Label>
              <Input
                id="reviewerName"
                type="text"
                value={reviewerName}
                onChange={(e) => setReviewerName(e.target.value)}
                required
                placeholder="Jean D."
                data-testid="reviewer-name-input"
              />
            </div>

            {/* Comment */}
            <div>
              <Label htmlFor="comment" className="text-slate-700 mb-2 block">
                Votre commentaire
              </Label>
              <textarea
                id="comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                required
                rows={4}
                className="w-full px-4 py-3 rounded-lg border border-slate-200 text-slate-900 focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                placeholder="Partagez votre expérience avec cet entrepreneur..."
                data-testid="review-comment-input"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting || rating === 0}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-6"
              data-testid="submit-review-button"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                "Envoyer mon avis"
              )}
            </Button>
          </form>
        </div>

        <p className="text-center text-sm text-slate-500 mt-6">
          <a href="/" className="hover:text-slate-700">
            ← Retour au site DevisMaison.be
          </a>
        </p>
      </div>
    </div>
  );
}
